import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { sessionVariables } from '../../app/session/session.variables';
import { userVariables } from '../../app/user.variables';
import { id } from '../interfaces/id.type';
import {
  ILoginCredentials,
  ImpersonateEmail,
  UpdatePasswordDTO,
} from './interfaces/login-credentials.interface';
import { RegisterDTO } from './interfaces/register.interface';
import { ResetPasswordDTO } from './interfaces/reset-password.interface';
import { ICreatePassword, ISession } from './interfaces/session.interface';
import { IUser, IUserProfileDTO, UpdateUserDTO } from './interfaces/user.interface';

@Injectable()
export class SessionApiService {
  private readonly httpClient = inject(HttpClient);

  readonly api = sessionVariables.apiUrl;
  readonly userApi = sessionVariables.userUrl;

  register(data: RegisterDTO, tenantId: id = 'public'): Observable<void> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.post<void>(`${this.api}/auth/register`, { ...data }, { headers });
  }

  validateRegister(token: string, tenantId: id = 'public'): Observable<void> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.post<void>(
      `${this.api}/auth/validateRegister/${token}`,
      {},
      { headers }
    );
  }

  login(credentials: ILoginCredentials, tenantId: id = 'public'): Observable<ISession> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.post<ISession>(
      `${this.api}/auth/login`,
      {
        ...credentials,
      },
      { headers }
    );
  }

  logout(tenantId: id = 'public'): Observable<unknown> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.delete<unknown>(`${this.api}/auth/logout`, { headers });
  }

  resetPasswordRequest(email: string, tenantId: id = 'public'): Observable<string> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.post<string>(
      `${this.api}/auth/request-password-recovery`,
      {
        email: email,
      },
      { headers }
    );
  }

  resetPassword(newCredentials: ResetPasswordDTO, tenantId: id = 'public'): Observable<ISession> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.post<ISession>(
      `${this.api}/auth/recoveryChangePassword`,
      {
        ...newCredentials,
      },
      { headers }
    );
  }

  updateUser(user: UpdateUserDTO, tenantId: id = 'public'): Observable<IUser> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    const userId = user.id;
    return this.httpClient.patch<IUser>(
      `${this.userApi}/${userId}`,
      {
        ...user,
      },
      { headers }
    );
  }

  changePassword(
    newCredentials: UpdatePasswordDTO,
    tenantId: id = 'public'
  ): Observable<ILoginCredentials> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.put<ILoginCredentials>(
      `${this.api}/session/password`,
      {
        ...newCredentials,
      },
      { headers }
    );
  }

  createPassword(
    createPasswordCredentials: ICreatePassword,
    tenantId: id = 'public'
  ): Observable<void> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.patch<void>(
      `${this.api}/auth/update-password`,
      createPasswordCredentials,
      { headers }
    );
  }

  editProfile(profileInfoDto: IUserProfileDTO, tenantId: id = 'public'): Observable<IUser> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.patch<IUser>(
      userVariables.profileUrl,
      { ...profileInfoDto },
      { headers }
    );
  }

  getProfile(tenantId: id = 'public'): Observable<IUser> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.get<IUser>(userVariables.profileUrl, { headers });
  }

  impersonate(credentials: ImpersonateEmail, tenantId: id = 'public'): Observable<ISession> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.post<ISession>(
      `${this.api}/auth/impersonate`,
      {
        ...credentials,
      },
      { headers }
    );
  }

  loginWithToken(token: string, tenantId: id = 'public'): Observable<ISession> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.post<ISession>(
      `${this.api}/session/login`,
      {
        sessionToken: token,
      },
      { headers }
    );
  }
}
